/**
 * DataTable Component
 *
 * Server-side data table component that works with the useDataGrid hook.
 * Displays data in a table format with support for sorting, filtering, and pagination.
 * All data fetching and state management is handled externally via the useDataGrid hook.
 */

import * as React from 'react';
import {
  type ColumnDef,
  type Column,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, ArrowUp, ArrowDown, Filter } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@loader/ui';

import type { DataGridSortDirection, DataGridFilter } from './types';
import { cn } from './utils';

// Context to drive header interactions (sorting and filtering)
const HeaderActionsContext = React.createContext<{
  sortBy?: string;
  sortDir?: DataGridSortDirection;
  onSortChange?: (
    sortBy: string | undefined,
    sortDir?: DataGridSortDirection
  ) => void;
  filters: DataGridFilter[];
  onFilterChange?: (filter: DataGridFilter) => void;
  onFilterRemove?: (columnId: string) => void;
}>({
  sortBy: undefined,
  sortDir: undefined,
  onSortChange: undefined,
  filters: [],
  onFilterChange: undefined,
  onFilterRemove: undefined,
});

/**
 * Props for the DataTable component
 *
 * The component expects data and state to be managed externally,
 * typically via the useDataGrid hook.
 */
interface DataTableProps<TData> {
  /** Column definitions for the table */
  columns: ColumnDef<TData>[];
  /** Data items to display (from useDataGrid) */
  data: TData[];
  /** Loading state indicator */
  isLoading?: boolean;
  /** Error state */
  error?: Error | null;
  /** Is currently fetching (including background refetch) */
  isFetching?: boolean;

  // Pagination props
  /** Current page index (0-based) */
  pageIndex: number;
  /** Items per page */
  pageSize: number;
  /** Total number of items available */
  totalItems: number;
  /** Number of pages available */
  pageCount: number;
  /** Callback when page changes */
  onPageChange?: (pageIndex: number) => void;
  /** Callback when page size changes */
  onPageSizeChange?: (pageSize: number) => void;
  /** Available page size options */
  pageSizeOptions?: number[];

  // Sorting props
  /** Current sort column ID */
  sortBy?: string;
  /** Current sort direction */
  sortDir?: DataGridSortDirection;
  /** Callback when sort changes */
  onSortChange?: (
    sortBy: string | undefined,
    sortDir?: DataGridSortDirection
  ) => void;

  // Filtering props
  /** Current active filters */
  filters?: DataGridFilter[];
  /** Callback when filter is added/updated */
  onFilterChange?: (filter: DataGridFilter) => void;
  /** Callback when filter is removed */
  onFilterRemove?: (columnId: string) => void;
  /** Callback to clear all filters */
  onFiltersClear?: () => void;

  // Feature toggles
  /** Enable filtering (default: true) */
  enableFiltering?: boolean;
  /** Enable pagination (default: true) */
  enablePagination?: boolean;

  // Styling options
  /** Additional CSS class names */
  className?: string;
  /** Height of the table container */
  height?: string;
  /** Hide vertical lines between columns (default: false) */
  hideVerticalLines?: boolean;
  /** Show footer with pagination and stats (default: true) */
  showFooter?: boolean;
  /** Visual variant: 'card' (default) has border/rounded/shadow, 'flush' has none */
  variant?: 'card' | 'flush';

  // Custom renderers
  /** Custom component to show when no data is available */
  emptyStateComponent?: React.ReactNode;
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
  /** Custom error component */
  errorComponent?: React.ReactNode;

  // Row interaction
  /** Callback when a row is clicked */
  onRowClick?: (row: TData) => void;
}

/**
 * DataTable Component
 *
 * A flexible, server-side data table component designed to work with the useDataGrid hook.
 *
 * @example
 * ```tsx
 * const grid = useDataGrid<User>({
 *   endpoint: DATA_GRID_ENDPOINTS.MEMBERSHIPS,
 * });
 *
 * return (
 *   <DataTable
 *     columns={columns}
 *     data={grid.data}
 *     isLoading={grid.isLoading}
 *     pageIndex={grid.pagination.pageIndex}
 *     pageSize={grid.pagination.pageSize}
 *     totalItems={grid.pagination.total}
 *     pageCount={grid.pagination.pageCount}
 *     onPageChange={grid.actions.setPage}
 *     onPageSizeChange={grid.actions.setPageSize}
 *     sortBy={grid.sorting.sortBy}
 *     sortDir={grid.sorting.sortDir}
 *     onSortChange={grid.actions.setSort}
 *     filters={grid.filters}
 *     onFilterChange={grid.actions.setFilter}
 *     onFilterRemove={grid.actions.removeFilter}
 *     onFiltersClear={grid.actions.clearFilters}
 *   />
 * );
 * ```
 */
export function DataTable<TData>({
  columns,
  data,
  isLoading = false,
  error = null,
  isFetching = false,
  // Pagination
  pageIndex,
  pageSize,
  totalItems,
  pageCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30, 50, 100],
  // Sorting
  sortBy,
  sortDir,
  onSortChange: _onSortChange,
  // Filtering
  filters = [],
  onFilterChange,
  onFilterRemove,
  onFiltersClear,
  // Features
  enableFiltering = true,
  enablePagination = true,
  // Styling
  className = '',
  height = '100%',
  hideVerticalLines = false,
  showFooter = true,
  variant = 'card',
  // Custom renderers
  emptyStateComponent,
  loadingComponent,
  errorComponent,
  // Row interaction
  onRowClick,
}: DataTableProps<TData>) {
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  // Setup TanStack Table
  const table = useReactTable({
    data: data || [],
    columns,
    getRowId: (row, index) =>
      ((row as Record<string, unknown>).id as string) ?? `row-${index}`,
    getCoreRowModel: getCoreRowModel(),
    // Server-side features
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount,
    // State
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting: sortBy ? [{ id: sortBy, desc: sortDir === 'desc' }] : [],
    },
  });

  // Variant-based styling
  const isCard = variant === 'card';
  const outerClassName = isCard ? 'h-full p-1' : 'h-full';
  const innerClassName = cn(
    'bg-card flex h-full flex-col overflow-hidden',
    isCard && 'rounded-lg border shadow-md',
    className
  );

  // Render error state
  if (error && errorComponent) {
    return (
      <div className={outerClassName}>
        <div className={innerClassName} style={{ height }}>
          <div className="flex flex-1 items-center justify-center p-8">
            {errorComponent}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={outerClassName}>
        <div className={innerClassName} style={{ height }}>
          <div className="flex flex-1 items-center justify-center p-8">
            <div className="text-destructive text-center">
              <p className="font-semibold">Error loading data</p>
              <p className="text-muted-foreground mt-2 text-sm">
                {error.message}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={outerClassName}>
      <div className={innerClassName} style={{ height }}>
        {/* Scrollable Table Container */}
        <div ref={tableContainerRef} className="flex-1 overflow-auto">
          <HeaderActionsContext.Provider
            value={{
              sortBy,
              sortDir,
              onSortChange: _onSortChange,
              filters,
              onFilterChange,
              onFilterRemove,
            }}
          >
            <table className="w-full min-w-max border-collapse">
              <thead className="bg-muted sticky top-0 z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={cn(
                          'text-muted-foreground border-b-2 px-4 py-2 text-left text-sm font-medium whitespace-nowrap',
                          !hideVerticalLines && 'border-r last:border-r-0'
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-card relative">
                {/* Loading overlay */}
                {isLoading && (
                  <tr>
                    <td colSpan={columns.length} className="px-4 py-12">
                      <div className="flex items-center justify-center">
                        {loadingComponent ?? (
                          <div className="flex flex-col items-center gap-2">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                            <span className="text-muted-foreground text-sm">
                              Loading data...
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {/* Data rows */}
                {!isLoading &&
                  table.getRowModel().rows &&
                  table.getRowModel().rows.length > 0 &&
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={cn(
                        'hover:bg-muted/50 border-b transition-colors',
                        onRowClick && 'cursor-pointer',
                        isFetching && 'opacity-60'
                      )}
                      onClick={() => onRowClick?.(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cn(
                            'text-foreground px-4 py-2 text-sm whitespace-nowrap',
                            !hideVerticalLines && 'border-r last:border-r-0'
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}

                {/* Empty state */}
                {!isLoading && table.getRowModel().rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-muted-foreground px-4 py-6 text-center text-sm"
                    >
                      {emptyStateComponent ?? 'No results found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </HeaderActionsContext.Provider>
        </div>

        {/* Footer with pagination and stats */}
        {showFooter && (enablePagination || enableFiltering) && (
          <div className="bg-muted/50 border-t px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              {/* Left: Row info and clear filters */}
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <div className="whitespace-nowrap">
                  {filters.length > 0
                    ? `${data.length} of ${totalItems} rows (filtered)`
                    : `${totalItems} rows`}
                </div>
                {enableFiltering && filters.length > 0 && onFiltersClear && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onFiltersClear}
                    className="h-6 px-2"
                  >
                    Clear filters
                  </Button>
                )}
              </div>

              {/* Center: Pagination */}
              {enablePagination && onPageChange && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => onPageChange(Math.max(0, pageIndex - 1))}
                        className={
                          pageIndex === 0
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }
                      />
                    </PaginationItem>

                    {Array.from({ length: pageCount }, (_, i) => i).map(
                      (idx) => {
                        // Show first page, last page, current page, and pages around current
                        if (
                          idx === 0 ||
                          idx === pageCount - 1 ||
                          (idx >= pageIndex - 1 && idx <= pageIndex + 1)
                        ) {
                          return (
                            <PaginationItem key={idx}>
                              <PaginationLink
                                onClick={() => onPageChange(idx)}
                                isActive={idx === pageIndex}
                                className="cursor-pointer"
                              >
                                {idx + 1}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        } else if (
                          idx === pageIndex - 2 ||
                          idx === pageIndex + 2
                        ) {
                          return (
                            <PaginationItem key={idx}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          );
                        }
                        return null;
                      }
                    )}

                    <PaginationItem>
                      <PaginationNext
                        onClick={() =>
                          onPageChange(Math.min(pageCount - 1, pageIndex + 1))
                        }
                        className={
                          pageIndex >= pageCount - 1
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}

              {/* Right: Rows per page selector */}
              {enablePagination && onPageSizeChange && (
                <div className="text-muted-foreground flex items-center gap-1 text-sm">
                  <span className="whitespace-nowrap">Rows:</span>
                  <Select
                    value={`${pageSize}`}
                    onValueChange={(value) => onPageSizeChange(Number(value))}
                  >
                    <SelectTrigger className="h-6 w-[80px] text-sm">
                      <SelectValue placeholder={pageSize} />
                    </SelectTrigger>
                    <SelectContent side="top">
                      {pageSizeOptions.map((size) => (
                        <SelectItem key={size} value={`${size}`}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * SortableHeader Component
 *
 * A header cell component that provides sorting and filtering controls.
 * Used in column definitions to add interactive sorting and filtering.
 *
 * @example
 * ```tsx
 * const columns: ColumnDef<User>[] = [
 *   {
 *     accessorKey: 'name',
 *     header: ({ column }) => (
 *       <SortableHeader column={column} onSort={handleSort} onFilter={handleFilter}>
 *         Name
 *       </SortableHeader>
 *     ),
 *   },
 * ];
 * ```
 */
export function SortableHeader<TData>({
  column,
  children,
  onSort,
  onFilter,
  sortBy,
  sortDir,
  filterValue,
}: {
  column: Column<TData, unknown>;
  children: React.ReactNode;
  onSort?: (columnId: string) => void;
  onFilter?: (columnId: string, value: string) => void;
  sortBy?: string;
  sortDir?: DataGridSortDirection;
  filterValue?: string;
}) {
  // Try to consume header actions from context if explicit props not provided
  const headerCtx = React.useContext(HeaderActionsContext);

  const effectiveSortBy = sortBy ?? headerCtx?.sortBy;
  const effectiveSortDir = sortDir ?? headerCtx?.sortDir;

  const handleSortClick = React.useCallback(() => {
    if (onSort) {
      onSort(column.id);
      return;
    }
    if (!headerCtx?.onSortChange) return;
    if (effectiveSortBy === column.id) {
      if (effectiveSortDir === 'asc') {
        headerCtx.onSortChange(column.id, 'desc');
      } else {
        headerCtx.onSortChange(undefined);
      }
    } else {
      headerCtx.onSortChange(column.id, 'asc');
    }
  }, [onSort, headerCtx, effectiveSortBy, effectiveSortDir, column.id]);

  const currentFilterValue =
    filterValue ??
    (headerCtx?.filters || [])
      .find((f) => f.column_id === column.id)
      ?.value?.toString() ??
    '';

  const [filterDropdownOpen, setFilterDropdownOpen] = React.useState(false);
  const [localFilterValue, setLocalFilterValue] =
    React.useState(currentFilterValue);

  const isActiveSort = effectiveSortBy === column.id;

  // Determine which icon to show based on sort state
  const SortIcon = !isActiveSort
    ? ArrowUpDown
    : effectiveSortDir === 'desc'
      ? ArrowDown
      : ArrowUp;

  return (
    <div className="flex items-center">
      {(onSort || headerCtx?.onSortChange) && (
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'data-[state=open]:bg-accent -ml-3 h-8',
            isActiveSort && 'text-primary'
          )}
          onClick={handleSortClick}
        >
          {children}
          <SortIcon className="ml-2 h-4 w-4" />
        </Button>
      )}

      {!onSort && !headerCtx?.onSortChange && (
        <span className="ml-1">{children}</span>
      )}

      {(onFilter || headerCtx?.onFilterChange) && (
        <DropdownMenu
          open={filterDropdownOpen}
          onOpenChange={setFilterDropdownOpen}
        >
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                '-ml-3 h-8 w-8 p-0',
                localFilterValue && 'text-primary'
              )}
            >
              <Filter className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="start">
            <div className="p-3">
              <Label
                htmlFor={column.id}
                className="mb-2 block text-sm font-medium capitalize"
              >
                Filter {children}
              </Label>
              <Input
                id={column.id}
                placeholder={`Filter ${children}...`}
                value={localFilterValue}
                onChange={(event) => setLocalFilterValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    if (onFilter) {
                      onFilter(column.id, localFilterValue);
                    } else if (headerCtx?.onFilterChange) {
                      headerCtx.onFilterChange({
                        column_id: column.id,
                        value: localFilterValue,
                      });
                    }
                    setFilterDropdownOpen(false);
                  }
                }}
                className="h-8"
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setLocalFilterValue('');
                    if (onFilter) {
                      onFilter(column.id, '');
                    } else if (headerCtx?.onFilterChange) {
                      if (headerCtx?.onFilterRemove) {
                        headerCtx.onFilterRemove(column.id);
                      } else {
                        headerCtx.onFilterChange({
                          column_id: column.id,
                          value: '',
                        });
                      }
                    }
                    setFilterDropdownOpen(false);
                  }}
                >
                  Clear
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    if (onFilter) {
                      onFilter(column.id, localFilterValue);
                    } else if (headerCtx?.onFilterChange) {
                      headerCtx.onFilterChange({
                        column_id: column.id,
                        value: localFilterValue,
                      });
                    }
                    setFilterDropdownOpen(false);
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
